import { stringIsNullOrWhitespace } from '@deltek/specification-client/core/Utilities/stringUtils'
import {
    ProductListingDetailConsolidatedProductMedia as ConsolidatedProductMedia,
    ProductListingDetailVideoSource,
} from '@deltek/specification-client/core/Widgets/ProductListing/Details/Media/productListingDetailConsolidatedProductMedia'
import { convertVideoDetailToLinks } from '@deltek/specification-client/core/Widgets/ProductListing/Details/productDetailUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { v4 as newGuid } from 'uuid'
import { BlueActionButton } from '../BlueActionButton'
import TabResultArea from './TabResultArea'
import VideoLinkItem, { VideoLinkProps } from './VideoLinkItem'

const videosTabContentStyles = makeStyles((theme: Theme) => ({
    addNewLink: {
        marginTop: theme.spacing(2),
    },
}))

/**
 * Validates the domain if it exists in the url.  Doesn't need to be well formed URL
 */
export const validateGoogleVimeoDomain = (url: string) =>
    url.toLocaleLowerCase().includes('youtube.com') ||
    url.toLocaleLowerCase().includes('youtu.be') ||
    url.toLocaleLowerCase().includes('vimeo.com')

export const getVideoSource = (url: string) => {
    return (url.toLocaleLowerCase().includes('vimeo.com')
        ? 'Vimeo'
        : 'YouTube') as ProductListingDetailVideoSource
}

// Extracts the video ID from a given YouTube or Vimeo URL.
export const getVideoId = (url: string) => {
    const lowerUrl = url.toLowerCase()

    if (lowerUrl.includes('youtu.be')) {
        return url.substring(url.lastIndexOf('/') + 1)
    }

    const vimeoLink = 'vimeo.com'
    if (lowerUrl.includes(vimeoLink)) {
        const [id, hash] = url
            .substring(url.lastIndexOf(vimeoLink) + vimeoLink.length + 1)
            .split('/')
        return hash ? `${id}?h=${hash}` : id
    }

    const params = new URLSearchParams(url.substring(url.indexOf('?')))
    return params.get('v') ?? ''
}

export interface VideoUpdateProps {
    productVideoObj?: Record<string, ConsolidatedProductMedia>
    removedId?: string
}

export interface VideosTabContentProps {
    onUpdate: (updateProps: VideoUpdateProps) => void
    productVideos: Record<string, ConsolidatedProductMedia>
    totalMediaDialogConsolidatedProductMedia: number
}

const VideosTabContent: React.FC<VideosTabContentProps> = ({
    onUpdate,
    productVideos,
    totalMediaDialogConsolidatedProductMedia,
}) => {
    const classes = videosTabContentStyles()
    const [videoLinkItems, setVideoLinkItems] = useState<
        Record<string, VideoLinkProps>
    >({})

    const videoLinkItemIds = Object.keys(videoLinkItems)

    const validateVideoLinkItems = () => {
        // check if everything pass validation
        let updatedObj: Record<string, VideoLinkProps> = {}
        let hasInvalidItem: boolean = false
        videoLinkItemIds.forEach((id) => {
            const currentVideoLinkItem = videoLinkItems[id]
            updatedObj[id] = {
                link: currentVideoLinkItem.link,
                order: currentVideoLinkItem.order,
                validLink:
                    currentVideoLinkItem.validLink &&
                    validateGoogleVimeoDomain(currentVideoLinkItem.link),
            }

            if (!hasInvalidItem && !updatedObj[id].validLink)
                hasInvalidItem = true
        })

        if (hasInvalidItem) {
            setVideoLinkItems(updatedObj)
        }

        return hasInvalidItem
    }

    const handleAddNewLink = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const hasInvalidItem = validateVideoLinkItems()

        if (hasInvalidItem) return

        setVideoLinkItems({
            ...videoLinkItems,
            [`video-${newGuid()}`]: {
                link: '',
                order: totalMediaDialogConsolidatedProductMedia,
                validLink: true,
            },
        })
    }

    const handleOnRemove = (videoRecordId: string) => {
        const updatedVideoLinkItems = { ...videoLinkItems }
        delete updatedVideoLinkItems[videoRecordId]

        onUpdate({ removedId: videoRecordId } as VideoUpdateProps)

        const updatedVideoLinkItemIds = Object.keys(updatedVideoLinkItems)
        if (updatedVideoLinkItemIds.length === 0) {
            setVideoLinkItems({
                [`video-${newGuid()}`]: {
                    link: '',
                    order: totalMediaDialogConsolidatedProductMedia,
                    validLink: true,
                },
            })
            return
        }

        setVideoLinkItems(updatedVideoLinkItems)
    }

    const handleOnValueChange = (value: Record<string, VideoLinkProps>) => {
        const currentId = Object.keys(value)[0]
        const currentVideoLink = value[currentId]

        const videoId = getVideoId(currentVideoLink.link)

        // no validation when link is empty and only one item
        currentVideoLink.validLink =
            videoLinkItemIds.length === 1 && currentVideoLink.link === ''
                ? true
                : currentVideoLink.validLink &&
                  !stringIsNullOrWhitespace(videoId)

        setVideoLinkItems({
            ...videoLinkItems,
            [currentId]: {
                ...currentVideoLink,
            },
        })

        if (videoLinkItemIds.length === 1 && currentVideoLink.link === '')
            return

        if (!currentVideoLink.validLink) return

        const fromSource = getVideoSource(currentVideoLink.link)

        const imgUrl =
            fromSource === 'YouTube'
                ? `https://img.youtube.com/vi/${videoId}/0.jpg`
                : ''

        onUpdate({
            productVideoObj: {
                [currentId]: {
                    imgUrl,
                    order: currentVideoLink.order,
                    thumbnailUrl: imgUrl,
                    thumbnailUrlFetching: fromSource === 'Vimeo', // it has to get the thumbnail using their API
                    fromSource,
                    videoId,
                    originalFilename: currentVideoLink.link,
                },
            },
        } as VideoUpdateProps)
    }

    useEffect(() => {
        const productVideoKeys = Object.keys(productVideos)
        if (
            productVideoKeys.length === 0 &&
            totalMediaDialogConsolidatedProductMedia < 10
        ) {
            setVideoLinkItems({
                [`video-${newGuid()}`]: {
                    link: '',
                    order: totalMediaDialogConsolidatedProductMedia,
                    validLink: true,
                },
            })
        } else {
            const currentVideoLinkItems: Record<string, VideoLinkProps> = {}
            productVideoKeys.forEach((key) => {
                const currentProductVideo = productVideos[key]
                currentVideoLinkItems[key] = {
                    link: convertVideoDetailToLinks({
                        fromSource: currentProductVideo.fromSource ?? '',
                        videoId: currentProductVideo.videoId ?? '',
                        videoStillImageUrl: currentProductVideo.thumbnailUrl,
                    }),
                    order: currentProductVideo.order,
                    validLink: true,
                }
            })
            setVideoLinkItems(currentVideoLinkItems)
        }
    }, [productVideos, totalMediaDialogConsolidatedProductMedia])

    return (
        <TabResultArea
            title={`${totalMediaDialogConsolidatedProductMedia} of 10 photos/videos added`}
        >
            <>
                {Object.keys(videoLinkItems).map((videoLinkItemKey, idx) => {
                    const videoLinkItemProps = videoLinkItems[videoLinkItemKey]
                    return (
                        <VideoLinkItem
                            key={videoLinkItemKey}
                            defaultVideoLinkProps={videoLinkItemProps}
                            id={videoLinkItemKey}
                            onRemove={handleOnRemove}
                            onValueChange={handleOnValueChange}
                            order={videoLinkItemProps.order}
                            showDivider={idx < videoLinkItemIds.length - 1}
                        />
                    )
                })}
                <BlueActionButton
                    additionalClassName={classes.addNewLink}
                    actionButtonProps={{
                        onClick: handleAddNewLink,
                        disabled:
                            totalMediaDialogConsolidatedProductMedia >= 10,
                    }}
                >
                    Add New Video
                </BlueActionButton>
            </>
        </TabResultArea>
    )
}

export default VideosTabContent
